/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/**
 * Webview detection.
 *
 * The portal is embedded inside the native app's webview by loading a direct
 * URL with a `?webview=true` marker (navigation is handled natively, so the
 * sidebar is hidden and pages render their mobile layout). Whether to show the
 * mobile design is therefore driven by *where* the page is loaded, NOT by the
 * screen width.
 *
 * The marker is only guaranteed to be present on the initial load, so the
 * result is cached in `sessionStorage` (scoped to the webview's session) to
 * survive client-side route changes that drop the query param.
 */
export const WEBVIEW_QUERY_PARAM = "webview"
export const WEBVIEW_BODY_CLASS = "webview-mode"
const WEBVIEW_SESSION_KEY = "mp_is_webview"

const readFromQuery = (): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get(WEBVIEW_QUERY_PARAM) ===
      "true"
    )
  } catch {
    return false
  }
}

/**
 * Returns true when the portal is running inside the native app webview.
 * Result is resolved once per session and cached.
 */
export const isWebview = (): boolean => {
  if (typeof window === "undefined") return false

  try {
    // Once resolved to webview, stay sticky for the session so client-side
    // route changes that drop the query param don't lose the mode. A cached
    // `false`, however, is re-evaluated below so a later `?webview=true` marker
    // in the same tab can still upgrade the mode.
    const cached = window.sessionStorage?.getItem(WEBVIEW_SESSION_KEY)
    if (cached === "true") return true
  } catch {
    // sessionStorage may be unavailable (private mode / sandboxed iframe) —
    // fall back to reading the query param directly.
    return readFromQuery()
  }

  const result = readFromQuery()
  try {
    window.sessionStorage?.setItem(WEBVIEW_SESSION_KEY, String(result))
  } catch {
    // ignore write failures — detection still works for this load
  }
  return result
}

/**
 * Adds the `webview-mode` class to <body> when running in the webview, so
 * global/layout CSS can switch to the mobile design. Safe to call at bootstrap.
 */
export const applyWebviewBodyClass = (): void => {
  if (typeof document === "undefined") return
  if (isWebview()) {
    document.body.classList.add(WEBVIEW_BODY_CLASS)
  }
}

/**
 * The `ecom-page` body class lets global CSS release the desktop content-width
 * floor so eCom pages are RESPONSIVE at any width (see `body.ecom-page` in
 * styles/global.ts). Unlike the webview class, this is driven by the ROUTE, not by
 * the app context — call it at bootstrap and on every client-side route change with
 * whether the current path is an eCom route (see `isEcomPath` in constants/routes).
 */
export const ECOM_PAGE_BODY_CLASS = "ecom-page"

export const applyEcomPageBodyClass = (isEcomPage: boolean): void => {
  if (typeof document === "undefined") return
  document.body.classList.toggle(ECOM_PAGE_BODY_CLASS, isEcomPage)
}
